import { Alert, Button, Flex, Input } from '@mantine/core';
import React, { useState } from 'react';
import theme from 'theme';
import useLang from 'lang';
import SimpleLayout from 'layouts/SimpleLayout';
import H2SemiBold from 'theme/components/Typography/H2SemiBold';
import { IconChevronRight, IconInfoCircle, IconX } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { USERS_PAGE_PATH } from 'routes/paths';
import { useForm } from '@mantine/form';
import { useApi } from 'api/api-context';
import { notifications } from '@mantine/notifications';

/**
 * UserAdd page - Add new user to household
 */
const UserAdd: React.FC = () => {
  const { _t } = useLang();
  const navigate = useNavigate();
  const { getAction } = useApi();
  const [loading, setLoading] = useState(false);

  const form = useForm({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
    },
  });

  // Check if all required fields are filled
  const emptyFields = !form.values.firstName.trim() || !form.values.lastName.trim() || !form.values.email.trim();

  /**
   * Handle back button
   */
  const handleCancel = () => {
    navigate(USERS_PAGE_PATH.original);
  };

  /**
   * Handle form submit - Add new family member
   */
  const handleSubmit = (data: { firstName: string; lastName: string; email: string; phoneNumber: string }) => {
    setLoading(true);
    const addFamilyMember = getAction('AddFamilyMember');
    addFamilyMember({
      payload: {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phoneNumber: data.phoneNumber || undefined,
      },
    })
      .then(() => {
        notifications.show({
          title: _t('Success'),
          message: _t('Family member added successfully'),
          color: 'green',
        });
        navigate(USERS_PAGE_PATH.original);
      })
      .catch((err) => {
        // ClientError puts the actual message in 'info' field, not 'msg'
        const errorMessage =
          err.response?.data?.info || err.response?.data?.msg || _t('An error occurred while adding the family member');
        notifications.show({
          title: _t('Error'),
          message: errorMessage,
          color: 'red',
        });
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <SimpleLayout>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Flex p="1rem" gap="1.5rem" direction="column" pb="8rem">
            <H2SemiBold color={theme.black}>{_t('Add new user')}</H2SemiBold>
            <Input.Wrapper label={_t('First name')} required>
              <Input variant="primary" placeholder={_t('First name')} {...form.getInputProps('firstName')} required />
            </Input.Wrapper>
            <Input.Wrapper label={_t('Last name')} required>
              <Input variant="primary" placeholder={_t('Last name')} {...form.getInputProps('lastName')} required />
            </Input.Wrapper>
            <Input.Wrapper label={_t('Email')} required>
              <Input
                variant="primary"
                placeholder={_t('Email')}
                type="email"
                {...form.getInputProps('email')}
                required
              />
            </Input.Wrapper>
            <Input.Wrapper label={_t('Phone number')}>
              <Input
                variant="primary"
                placeholder={_t('Phone number')}
                type="tel"
                {...form.getInputProps('phoneNumber')}
              />
            </Input.Wrapper>
            <Alert
              icon={<IconInfoCircle size="24px" />}
              variant="default"
              style={{
                borderRadius: '12px',
                backgroundColor: '#E9EBEC',
                border: `1px solid ${theme.colors?.gray?.[2]}`,
                color: theme.colors?.gray?.[9],
              }}
              styles={{
                message: { color: '#525B6A' },
              }}
            >
              {_t('An activation link to create a password will be sent to the provided email address.')}
            </Alert>
          </Flex>
          <Flex
            justify="space-between"
            gap="1rem"
            px="1rem"
            w="100%"
            style={{
              position: 'fixed',
              bottom: '2.5rem',
            }}
          >
            <Button
              variant="secondary"
              w="100%"
              size="lg"
              leftSection={<IconX size="1.5rem" />}
              onClick={handleCancel}
              disabled={loading}
            >
              {_t('Cancel')}
            </Button>
            <Button
              variant="primary"
              w="100%"
              size="lg"
              rightSection={<IconChevronRight size="1.5rem" />}
              loading={loading}
              type="submit"
              disabled={emptyFields}
            >
              {_t('Send')}
            </Button>
          </Flex>
        </form>
      </SimpleLayout>
    </>
  );
};

export default UserAdd;
